// sites.$siteId.tsx — T046
// Route parente : /sites/:siteId — layout partagé par toutes les routes scoping "site".
// Guard : vérifie l'accès site ou région avant d'afficher les pages DUERP.

import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import { useAuth } from "@/application/contexts/AuthContext";
import { usePermissions } from "@/application/hooks/usePermissions";
// eslint-disable-next-line no-restricted-imports -- route loaders call API directly (TanStack Router pattern)
import { getSite } from "@/infrastructure/api/sites.api";
import { SitePage } from "@/presentation/pages/SitePage";

const siteSearchSchema = z.object({
  versionId: z.string().min(1).optional(),
});

interface SiteSearch {
  versionId?: string;
}

export const Route = createFileRoute("/sites/$siteId")({
  validateSearch: (search: Record<string, unknown>): SiteSearch => {
    const parsed = siteSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  loader: async ({ params }) => {
    const site = await getSite(params.siteId);
    return { site };
  },
  component: function SiteRouteComponent() {
    const { siteId } = Route.useParams();
    const { site } = Route.useLoaderData();
    const { versionId } = Route.useSearch();
    const { canAccessSite, isLoading } = useAuth();
    const permissions = usePermissions();
    const navigate = useNavigate();
    const pathname = useRouterState({
      select: (state) => state.location.pathname,
    });
    const isSiteHomePath =
      pathname === `/sites/${siteId}` || pathname === `/sites/${siteId}/`;
    const hasSiteAccess = canAccessSite(site.id, site.regionId);
    const hasRegionAccess = permissions.duerp.canViewRegionDashboard(
      site.regionId,
    );

    useEffect(() => {
      if (!isLoading && !hasSiteAccess && !hasRegionAccess) {
        void navigate({ to: "/" });
      }
    }, [hasRegionAccess, hasSiteAccess, isLoading, navigate]);

    return isSiteHomePath ? (
      <SitePage site={site} initialVersionId={versionId} />
    ) : (
      <Outlet />
    );
  },
});
